import React, { useMemo } from "react";
import { salesApi } from "../../../services/reportsService";
import { useReportData } from "../useReportData";
import ServerTable from "../ServerTable";
import {
  AlertStrip,
  BarList,
  DonutChart,
  ErrorState,
  Funnel,
  ItemList,
  KpiGrid,
  Leaderboard,
  Panel,
  ReportSkeleton,
  Row,
  SeriesLegend,
  StatusBadge,
  TrendChart,
  TablePanel,
  DistTable,
  TrendTable,
  cols,
  useReportContext,
} from "../ReportKit";
import { OPEN_ENQUIRY } from "../drillColumns";
import {
  PALETTE,
  buildKpis,
  formatDate,
  formatValue,
  num,
  numify,
  pickName,
  toDateString,
  toDist,
} from "../reportUtils";

const DEFS = [
  {
    key: "totalEnquiries",
    label: "Enquiries",
    desc: "Received {p}",
    icon: "mdi:file-document-outline",
  },
  {
    key: "openEnquiries",
    label: "Still open",
    desc: "Being worked on now",
    icon: "mdi:folder-open-outline",
  },
  {
    key: "approvedEnquiries",
    label: "Approved",
    desc: "Said yes {p}",
    icon: "mdi:check-decagram-outline",
  },
  {
    key: "rejectedEnquiries",
    label: "Rejected",
    desc: "Said no {p}",
    good: "DOWN",
    icon: "mdi:close-circle-outline",
  },
  {
    key: "winRate",
    label: "Win rate",
    desc: "Out of every 100 answered, how many said yes",
    unit: "PERCENT",
    icon: "mdi:trophy-outline",
  },
  {
    key: "pipelineValue",
    label: "Money in progress",
    desc: "Value of enquiries still open",
    unit: "CURRENCY",
    icon: "mdi:chart-timeline-variant",
  },
  {
    key: "wonValue",
    label: "Money won",
    desc: "Value of approved enquiries {p}",
    unit: "CURRENCY",
    icon: "mdi:cash-check",
  },
  {
    key: "avgDealSize",
    label: "Average deal",
    desc: "Money for each approved enquiry",
    unit: "CURRENCY",
    icon: "mdi:cash-multiple",
  },
  {
    key: "avgCycleDays",
    label: "Days to close",
    desc: "Average days to get a yes",
    unit: "DAYS",
    good: "DOWN",
    icon: "mdi:calendar-clock-outline",
  },
  {
    key: "highPriorityOpen",
    label: "Urgent",
    desc: "Open enquiries marked high priority",
    icon: "mdi:flag-outline",
  },
  {
    key: "overdueEnquiries",
    label: "Running late",
    desc: "Past the expected close date",
    good: "DOWN",
    icon: "mdi:alarm-light-outline",
  },
];

const enquiryListColumns = (currency) => [
  cols.text("enquiryCode", "Enquiry"),
  cols.text("clientName", "Client"),
  cols.text("stageName", "Stage"),
  cols.badge("priority", "Priority"),
  cols.num("totalEstimateAmount", "Estimate", "CURRENCY", currency),
  cols.num("totalQuotationAmount", "Quote", "CURRENCY", currency),
  cols.num("daysInCurrentStage", "Days in stage"),
];

const amount = (e) =>
  Number(e.totalQuotationAmount) || Number(e.totalEstimateAmount) || 0;

const EnquiriesView = ({ params, setFilter, onLoaded, refreshKey }) => {
  const { view } = useReportContext();
  const { data, loading, error, reload } = useReportData(
    () => salesApi.enquirySummary(params),
    [JSON.stringify(params), refreshKey],
    onLoaded,
  );
  const tableParams = useMemo(
    () => ({
      startDate: params.startDate,
      endDate: params.endDate,
      ownerId: params.ownerId,
      stageId: params.stageId,
      status: params.status,
      priority: params.priority,
      type: params.type,
    }),
    [params],
  );

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <ReportSkeleton />;

  const { meta = {}, charts = {}, lists = {} } = data;
  const currency = meta.currency || "INR";
  const kpis = buildKpis(data.kpis, DEFS);
  const overdue = kpis.find((k) => k.key === "overdueEnquiries")?.value || 0;
  const stuck = lists.stuckEnquiries || [];
  const trendRows = numify(charts.enquiryTrend);
  const flow = [
    { key: "created", label: "Created", color: PALETTE[0], type: "area" },
    { key: "approved", label: "Approved", color: PALETTE[1], type: "area" },
    { key: "rejected", label: "Rejected", color: PALETTE[4], type: "area" },
  ];
  const won = [
    {
      key: "wonValue",
      label: "Won value",
      color: PALETTE[1],
      type: "bar",
      unit: "CURRENCY",
    },
  ];
  const forecast = [
    {
      key: "expectedValue",
      label: "Expected",
      color: PALETTE[0],
      type: "bar",
      unit: "CURRENCY",
    },
    {
      key: "weightedValue",
      label: "Weighted",
      color: PALETTE[3],
      type: "line",
      unit: "CURRENCY",
    },
  ];

  const items = (rows = []) =>
    rows.map((e) => ({
      id: e.enquiryId,
      title: e.enquiryCode,
      subtitle: [e.clientName, e.stageName].filter(Boolean).join(", "),
      badge: e.priority,
      right: amount(e)
        ? formatValue(amount(e), "CURRENCY", currency)
        : `${e.daysInCurrentStage ?? "—"} days in stage`,
    }));

  const columns = [
    {
      key: "enquiryCode",
      header: "Enquiry",
      sortable: true,
      render: (v) => <strong>{v}</strong>,
    },
    { key: "clientName", header: "Client" },
    { key: "leadName", header: "Lead" },
    { key: "stageName", header: "Stage" },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (v) => <StatusBadge value={v} />,
    },
    {
      key: "priority",
      header: "Priority",
      sortable: true,
      render: (v) => <StatusBadge value={v} />,
    },
    { key: "type", header: "Type" },
    {
      key: "totalEstimateAmount",
      header: "Estimate",
      align: "right",
      render: (v) => formatValue(v, "CURRENCY", currency),
    },
    {
      key: "totalQuotationAmount",
      header: "Quotation",
      align: "right",
      render: (v) => formatValue(v, "CURRENCY", currency),
    },
    {
      key: "expectedCloseDate",
      header: "Expected close",
      render: (v) => formatDate(v),
    },
    { key: "ageDays", header: "Age (days)", align: "right" },
    { key: "daysInCurrentStage", header: "In stage (days)", align: "right" },
    { key: "assignedEmployeeName", header: "Owner" },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      render: (v) => formatDate(v),
    },
  ];

  const today = toDateString(new Date());
  const answered = (r) => /APPROV|REJECT/i.test(r.status || "");
  const T = (title, how, p = {}, extra = {}) => ({
    modal: {
      title,
      how,
      columns,
      fetcher: salesApi.enquiryTable,
      params: { ...tableParams, ...p },
      sort: "createdAt,desc",
      searchable: true,
      ...extra,
    },
  });
  const details = {
    totalEnquiries: {
      scrollTo: "tbl-main",
      ...T("Enquiries", "These are the enquiries received {p}."),
    },
    openEnquiries: T(
      "Open enquiries",
      "These enquiries are still being worked on.",
      {},
      { filter: OPEN_ENQUIRY },
    ),
    approvedEnquiries: T(
      "Approved enquiries",
      "These enquiries got a yes {p}.",
      { status: "APPROVED" },
    ),
    rejectedEnquiries: T(
      "Rejected enquiries",
      "These enquiries got a no {p}.",
      { status: "REJECTED" },
    ),
    winRate: T(
      "Answered enquiries",
      "Win rate is the approved enquiries divided by all answered ones. These are the answered ones.",
      {},
      { filter: answered },
    ),
    pipelineValue: T(
      "Money in progress",
      "These open enquiries add up to the money in progress.",
      {},
      { filter: OPEN_ENQUIRY },
    ),
    wonValue: T(
      "Money won",
      "These approved enquiries add up to the money won {p}.",
      { status: "APPROVED" },
    ),
    avgDealSize: T(
      "Approved enquiries",
      "Divide the money won by these enquiries to get the average deal.",
      { status: "APPROVED" },
    ),
    avgCycleDays: T(
      "Approved enquiries",
      "Each took some days to get a yes. The average of those days is shown.",
      { status: "APPROVED" },
    ),
    highPriorityOpen: T(
      "Urgent open enquiries",
      "These open enquiries are marked high priority.",
      { priority: "HIGH" },
      { filter: OPEN_ENQUIRY },
    ),
    overdueEnquiries: T(
      "Enquiries running late",
      "These open enquiries are past their expected close date.",
      {},
      {
        filter: (r) =>
          OPEN_ENQUIRY(r) &&
          r.expectedCloseDate &&
          String(r.expectedCloseDate).slice(0, 10) < today,
      },
    ),
  };

  return (
    <div className={`rp-view ${loading ? "rp-refreshing" : ""}`}>
      <AlertStrip
        items={[
          overdue > 0 && {
            tone: "bad",
            icon: "mdi:alarm-light-outline",
            text: `${overdue} enquiries are past their expected close date`,
          },
          stuck.length > 0 && {
            tone: "warn",
            icon: "mdi:timer-alert-outline",
            text: `${stuck.length} enquiries are stuck in a stage`,
          },
        ]}
      />
      <KpiGrid kpis={kpis} currency={currency} details={details} />
      {view === "table" ? (
        <>
          <ServerTable
            id="tbl-main"
            title="All enquiries"
            columns={columns}
            params={tableParams}
            fetcher={salesApi.enquiryTable}
            defaultSort="createdAt,desc"
            exportName="enquiries"
            onData={onLoaded}
          />

          <div className="rp-tables">
            <TrendTable
              title="Enquiries by date"
              data={trendRows}
              series={[...flow, ...won]}
              granularity={meta.granularity}
              currency={currency}
            />
            <TablePanel
              title="Pipeline by stage"
              exportName="pipeline-stages"
              columns={[
                cols.text("stage", "Stage"),
                cols.num("count", "Enquiries"),
                cols.num("value", "Money", "CURRENCY", currency),
                cols.num("conversion", "From last stage %", "PERCENT"),
                cols.num("days", "Days in stage", "DECIMAL"),
              ]}
              rows={(charts.funnelByStage || []).map((s) => ({
                stage: pickName(s),
                count: num(s, "count"),
                value: num(s, "value"),
                conversion: num(s, "conversionFromPrevious"),
                days: num(s, "avgDaysInStage"),
              }))}
            />
            <DistTable
              title="By status"
              data={toDist(charts.byStatus)}
              nameHeader="Status"
              valueHeader="Enquiries"
            />
            <DistTable
              title="By priority"
              data={toDist(charts.byPriority)}
              nameHeader="Priority"
              valueHeader="Enquiries"
            />
            <DistTable
              title="By type"
              data={toDist(charts.byType)}
              nameHeader="Type"
              valueHeader="Enquiries"
            />
            <DistTable
              title="Money by stage"
              data={toDist(charts.valueByStage, [
                "value",
                "totalValue",
                "count",
              ])}
              nameHeader="Stage"
              valueHeader="Money"
              unit="CURRENCY"
              currency={currency}
              share={false}
            />
            <TablePanel
              title="Owner performance"
              exportName="enquiry-owners"
              columns={[
                cols.text("name", "Owner"),
                cols.num("enq", "Enquiries"),
                cols.num("approved", "Approved"),
                cols.num("rate", "Win rate", "PERCENT"),
                cols.num("won", "Money won", "CURRENCY", currency),
                cols.num("pipeline", "Money open", "CURRENCY", currency),
              ]}
              rows={(charts.byOwner || []).map((o) => ({
                name: pickName(o),
                enq: num(o, "enquiries"),
                approved: num(o, "approved"),
                rate: num(o, "winRate"),
                won: num(o, "wonValue"),
                pipeline: num(o, "pipelineValue"),
              }))}
            />
            <DistTable
              title="Win rate by source"
              data={toDist(charts.winRateBySource, ["winRate", "value"])}
              nameHeader="Source"
              valueHeader="Win rate"
              unit="PERCENT"
              share={false}
            />
            <DistTable
              title="Top clients"
              data={toDist(charts.byClient)}
              nameHeader="Client"
              valueHeader="Enquiries"
            />
            <DistTable
              title="Why enquiries are rejected"
              data={toDist(charts.rejectionReasons)}
              nameHeader="Reason"
              valueHeader="Enquiries"
            />
            <TrendTable
              title="Closing forecast"
              data={numify(charts.closingForecast)}
              series={forecast}
              granularity="MONTH"
              currency={currency}
            />
            <TablePanel
              title="Closing this week"
              exportName="closing-this-week"
              columns={enquiryListColumns(currency)}
              rows={lists.closingThisWeek || []}
            />
            <TablePanel
              title="Biggest open enquiries"
              exportName="biggest-open"
              columns={enquiryListColumns(currency)}
              rows={lists.highValueOpen || []}
            />
            <TablePanel
              title="Stuck in a stage"
              exportName="stuck-enquiries"
              columns={enquiryListColumns(currency)}
              rows={stuck}
            />
          </div>
        </>
      ) : (
        <>
          <Row layout="2-1">
            <Panel
              title="Enquiries over time"
              actions={<SeriesLegend series={flow} />}
            >
              <TrendChart
                data={trendRows}
                series={flow}
                granularity={meta.granularity}
              />
            </Panel>
            <Panel title="By status" subtitle="Select a status to filter">
              <DonutChart
                data={toDist(charts.byStatus)}
                onSelect={(v) => setFilter("status", v)}
              />
            </Panel>
          </Row>

          <Row layout="1-1">
            <Panel
              title="Pipeline by stage"
              subtitle="Count, value and time in stage"
            >
              <Funnel
                currency={currency}
                stages={(charts.funnelByStage || []).map((s) => ({
                  name: pickName(s),
                  count: Number(s.count) || 0,
                  value: num(s, "value"),
                  conversion: num(s, "conversionFromPrevious"),
                  days: num(s, "avgDaysInStage"),
                }))}
              />
            </Panel>
            <Panel title="Value won" actions={<SeriesLegend series={won} />}>
              <TrendChart
                data={trendRows}
                series={won}
                granularity={meta.granularity}
                currency={currency}
              />
            </Panel>
          </Row>

          <Row layout="1-1-1">
            <Panel title="By priority" subtitle="Select a priority to filter">
              <DonutChart
                data={toDist(charts.byPriority)}
                onSelect={(v) => setFilter("priority", v)}
              />
            </Panel>
            <Panel title="By type">
              <DonutChart
                data={toDist(charts.byType)}
                onSelect={(v) => setFilter("type", v)}
              />
            </Panel>
            <Panel title="Value by stage">
              <BarList
                unit="CURRENCY"
                currency={currency}
                color={PALETTE[3]}
                data={toDist(charts.valueByStage, [
                  "value",
                  "totalValue",
                  "count",
                ])}
              />
            </Panel>
          </Row>

          <Row layout="2-1">
            <Panel title="Owner performance" subtitle="Ranked by won value">
              <Leaderboard
                primaryUnit="CURRENCY"
                primaryLabel="won"
                currency={currency}
                rows={[...(charts.byOwner || [])]
                  .map((o) => ({
                    name: pickName(o),
                    primary: Number(o.wonValue) || Number(o.enquiries) || 0,
                    metrics: [
                      { label: "Enquiries", value: num(o, "enquiries") },
                      { label: "Approved", value: num(o, "approved") },
                      {
                        label: "Win rate",
                        value: num(o, "winRate"),
                        unit: "PERCENT",
                      },
                      {
                        label: "Pipeline",
                        value: num(o, "pipelineValue"),
                        unit: "CURRENCY",
                      },
                    ],
                  }))
                  .sort((a, b) => b.primary - a.primary)}
              />
            </Panel>
            <Panel title="Win rate by source">
              <BarList
                unit="PERCENT"
                max={100}
                color={PALETTE[1]}
                data={toDist(charts.winRateBySource, ["winRate", "value"])}
              />
            </Panel>
          </Row>

          <Row layout="1-1-1">
            <Panel title="Top clients">
              <BarList data={toDist(charts.byClient)} color={PALETTE[5]} />
            </Panel>
            <Panel title="Why enquiries are rejected">
              <BarList
                data={toDist(charts.rejectionReasons)}
                color={PALETTE[4]}
              />
            </Panel>
            <Panel
              title="Closing forecast"
              actions={<SeriesLegend series={forecast} />}
            >
              <TrendChart
                data={numify(charts.closingForecast)}
                series={forecast}
                granularity="MONTH"
                currency={currency}
                height={220}
              />
            </Panel>
          </Row>

          <Row layout="1-1-1">
            <Panel title="Closing this week">
              <ItemList
                empty="Nothing closes this week"
                items={items(lists.closingThisWeek)}
              />
            </Panel>
            <Panel title="Biggest open enquiries">
              <ItemList
                empty="No open enquiries"
                items={items(lists.highValueOpen)}
              />
            </Panel>
            <Panel title="Stuck in a stage">
              <ItemList empty="Every enquiry is moving" items={items(stuck)} />
            </Panel>
          </Row>
        </>
      )}
    </div>
  );
};

export default EnquiriesView;
